
// React Imports
import * as React from "react";
import {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";

// Mui Imports
import {
    DataGrid,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport,
    GridToolbarFilterButton
} from '@mui/x-data-grid';
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
    Alert, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu, Select,
    Stack,
    Tooltip
} from "@mui/material";

// App Imports

import {
    deleteFinancial, financialSearch, getFileIds, processFinancials
} from "./FinancialClient";

import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {changeFinancial} from "./financialSlice";
import {changeCrudSnackbar} from "../../CommonComponents/crudSnackbarSlice";
import FinancialDrawer from "./FinancialDrawer";
import CustomFooter from "../../CommonComponents/GridCustomFooter";
import CrudSnackbar from "../../CommonComponents/CrudSnackbar";
import {changeUploadFile} from "../../CommonComponents/UploadFile/uploadFileSlice";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import FinancialSearch from "./FinancialSearch";

import FormControl from "@mui/material/FormControl";
import LinearIndeterminate from "../../CommonComponents/Progress";
import AddFiltersExportToolbar from "../../CommonComponents/AddFiltersExportToolbar";
import {LicenseGridColumns} from "../License/LicenseGridColumns";
import {changeLicense} from "../License/licenseSlice";
import {FinancialGridColumns} from "./FinancialGridColumns";
import UploadFileDrawer from "../../CommonComponents/UploadFile/UploadFileDrawer";
import {changeFinancialSearch} from "./financialSearchSlice";
import Snackbar from "@mui/material/Snackbar";
import NoSearchCriteriaSnackbar from "../../CommonComponents/NoSearchCriteriaSnackbar";
import {changeExternalData} from "../externalDataSlice";
import FinancialInfoDialogue from "./Info/FinancialInfoDialogue";

// main function
export default function FinancialGrid() {

    // ****************** BEGIN STATES ****************************

    const [financials, setFinancials] = useState([]);
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [gridVisible, setGridVisible] = React.useState(true);
    const [showControls, setShowControls] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const hideActions = localStorage.getItem('hideActions') === "true";
    const [financialInfoOpen, setFinancialInfoOpen] = useState(false);

    // ****************** END STATES ****************************


    // *********************** BEGIN STORE ********************************


    const selectorFinancialId = useSelector((state) => state.financialSlc.value.id);

    const selectorFileId = useSelector((state) => state.financialSearchSlc.value.fileId);
    const selectorPropertyId = useSelector((state) => state.financialSearchSlc.value.propertyId);
    const selectorProductName = useSelector((state) => state.financialSearchSlc.value.productName);
    const selectorEpisodeName = useSelector((state) => state.financialSearchSlc.value.episodeName);
    const selectorPhotoStartDateFrom = useSelector((state) => state.financialSearchSlc.value.photoStartDateFrom);
    const selectorPhotoStartDateTo = useSelector((state) => state.financialSearchSlc.value.photoStartDateTo);
    const selectorSeriesExternalId = useSelector((state) => state.financialSearchSlc.value.seriesExternalId);
    const selectorSeriesName = useSelector((state) => state.financialSearchSlc.value.seriesName);
    const selectorSeasonExternalId = useSelector((state) => state.financialSearchSlc.value.seasonExternalId);
    const selectorSeasonNumberFrom = useSelector((state) => state.financialSearchSlc.value.seasonNumberFrom);
    const selectorSeasonNumberTo = useSelector((state) => state.financialSearchSlc.value.seasonNumberTo);
    const selectorSeasonName = useSelector((state) => state.financialSearchSlc.value.seasonName);
    const selectorAccount = useSelector((state) => state.financialSearchSlc.value.account);
    const selectorAmountFrom = useSelector((state) => state.financialSearchSlc.value.amountFrom);
    const selectorAmountTo = useSelector((state) => state.financialSearchSlc.value.amountTo);
    const selectorAmountType = useSelector((state) => state.financialSearchSlc.value.amountType);
    const selectorFinancialMarket = useSelector((state) => state.financialSearchSlc.value.financialMarket);
    const selectorTerritory = useSelector((state) => state.financialSearchSlc.value.territory);
    const selectorLanguageCode = useSelector((state) => state.financialSearchSlc.value.languageCode);
    const selectorLicenseId = useSelector((state) => state.financialSearchSlc.value.licenseId);
    const selectorLicensee = useSelector((state) => state.financialSearchSlc.value.licensee);
    const selectorPeriodEndDateFrom = useSelector((state) => state.financialSearchSlc.value.periodEndDateFrom);
    const selectorPeriodEndDateTo = useSelector((state) => state.financialSearchSlc.value.periodEndDateTo);
    const selectorSourceSystem = useSelector((state) => state.financialSearchSlc.value.sourceSystem);
    const selectorSourceSystemId = useSelector((state) => state.financialSearchSlc.value.sourceSystemId);
    const selectorUnitsFrom = useSelector((state) => state.financialSearchSlc.value.unitsFrom);
    const selectorUnitsTo = useSelector((state) => state.financialSearchSlc.value.unitsTo);
    const selectorAdjustBeginFrom = useSelector((state) => state.financialSearchSlc.value.adjustBeginFrom);
    const selectorAdjustBeginTo = useSelector((state) => state.financialSearchSlc.value.adjustBeginTo);
    const selectorAdjustEndFrom = useSelector((state) => state.financialSearchSlc.value.adjustEndFrom);
    const selectorAdjustEndTo = useSelector((state) => state.financialSearchSlc.value.adjustEndTo);
    const selectorAdjustNote = useSelector((state) => state.financialSearchSlc.value.adjustNote);
    const selectorStakeholderId = useSelector((state) => state.financialSearchSlc.value.stakeholderId);
    const selectorContractId = useSelector((state) => state.financialSearchSlc.value.contractId);
    const selectorParticipations = useSelector((state) => state.financialSearchSlc.value.participations);
    const selectorResiduals = useSelector((state) => state.financialSearchSlc.value.residuals);
    const selectorRoyalties = useSelector((state) => state.financialSearchSlc.value.royalties);
    const selectorIdFrom = useSelector((state) => state.financialSearchSlc.value.idFrom);
    const selectorIdTo = useSelector((state) => state.financialSearchSlc.value.idTo);
    const selectorDrawerState = useSelector((state) => state.financialSlc.value.financialDrawerState);

    const selectedId = useSelector((state) => state.financialSlc.value.id);

    const [fileIdDropdown, setFileIdDropdown] = useState([]);
    const [selectedFileId, setSelectedFileId] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const selectorUploadFileDrawerState = useSelector((state) => state.uploadFileSlc.value.uploadFileDrawerState);

    const dispatchData = useDispatch();

    const selectorFinancialGridRefresh = useSelector(
        (state) => state.financialSlc.value.financialGridRefresh
    );

    // ********************** END STORE ********************************


    // ****************** BEGIN FETCH ****************************

    // const fetchFinancials = () =>{
    //     // function fetchProducts() {
    //     if(
    //         selectorFileId !== null ||
    //         selectorPropertyId !== null ||
    //             selectorProductName !== null ||
    //             selectorEpisodeName !== null ||
    //             selectorPhotoStartDateFrom !== null ||
    //             selectorPhotoStartDateTo !== null ||
    //             selectorSeriesExternalId !== null ||
    //             selectorSeriesName !== null ||
    //             selectorSeasonExternalId !== null ||
    //             selectorSeasonNumberFrom !== null ||
    //             selectorSeasonNumberTo !== null ||
    //             selectorSeasonName !== null ||
    //             selectorAccount !== null ||
    //             selectorAmountFrom !== null ||
    //             selectorAmountTo !== null ||
    //             selectorAmountType !== null ||
    //             selectorFinancialMarket !== null ||
    //             selectorTerritory !== null ||
    //             selectorLanguageCode !== null ||
    //             selectorLicenseId !== null ||
    //             selectorLicensee !== null ||
    //             selectorPeriodEndDateFrom !== null ||
    //             selectorPeriodEndDateTo !== null ||
    //             selectorSourceSystem !== null ||
    //             selectorSourceSystemId !== null ||
    //             selectorUnitsFrom !== null ||
    //             selectorUnitsTo !== null ||
    //             selectorAdjustBeginFrom !== null ||
    //             selectorAdjustBeginTo !== null ||
    //             selectorAdjustEndFrom !== null ||
    //             selectorAdjustEndTo !== null ||
    //             selectorAdjustNote !== null ||
    //         selectorStakeholderId !== null ||
    //         selectorContractId !== null ||
    //             selectorParticipations !== null ||
    //             selectorResiduals !== null ||
    //             selectorRoyalties !== null
    //
    //     )
    //     {
    //         {
    //             financialSearch(
    //                 selectorFileId,
    //                 selectorPropertyId,
    //                 selectorProductName,
    //                 selectorEpisodeName,
    //                 selectorPhotoStartDateFrom,
    //                 selectorPhotoStartDateTo,
    //                 selectorSeriesExternalId,
    //                 selectorSeriesName,
    //                 selectorSeasonExternalId,
    //                 selectorSeasonNumberFrom,
    //                 selectorSeasonNumberTo,
    //                 selectorSeasonName,
    //                 selectorAccount,
    //                 selectorAmountFrom,
    //                 selectorAmountTo,
    //                 selectorAmountType,
    //                 selectorFinancialMarket,
    //                 selectorTerritory,
    //                 selectorLanguageCode,
    //                 selectorLicenseId,
    //                 selectorLicensee,
    //                 selectorPeriodEndDateFrom,
    //                 selectorPeriodEndDateTo,
    //                 selectorSourceSystem,
    //                 selectorSourceSystemId,
    //                 selectorUnitsFrom,
    //                 selectorUnitsTo,
    //                 selectorAdjustBeginFrom,
    //                 selectorAdjustBeginTo,
    //                 selectorAdjustEndFrom,
    //                 selectorAdjustEndTo,
    //                 selectorAdjustNote,
    //                 selectorStakeholderId,
    //                 selectorContractId,
    //                 selectorParticipations,
    //                 selectorResiduals,
    //                 selectorRoyalties
    //             )

    //                 .then(data => {
    //                         console.log(data);
    //                         setFinancials(data);
    //                     }
    //                 )
    //         }
    //     }
    //     else{console.log("search triggered, nothing to search.")}}
    const fetchFinancials = () => {

        const request = {

            fileId: selectorFileId,

            propertyId: selectorPropertyId,

            productName: selectorProductName,

            episodeName: selectorEpisodeName,

            photoStartDateFrom: selectorPhotoStartDateFrom,
            photoStartDateTo: selectorPhotoStartDateTo,

            seriesExternalId: selectorSeriesExternalId,
            seriesName: selectorSeriesName,

            seasonExternalId: selectorSeasonExternalId,

            seasonNumberFrom: selectorSeasonNumberFrom,
            seasonNumberTo: selectorSeasonNumberTo,

            seasonName: selectorSeasonName,

            account: selectorAccount,

            amountFrom: selectorAmountFrom,
            amountTo: selectorAmountTo,

            amountType: selectorAmountType,

            financialMarket: selectorFinancialMarket,

            territory: selectorTerritory,

            languageCode: selectorLanguageCode,

            licenseId: selectorLicenseId,

            licensee: selectorLicensee,

            periodEndDateFrom: selectorPeriodEndDateFrom,
            periodEndDateTo: selectorPeriodEndDateTo,

            idFrom: selectorIdFrom,
            idTo: selectorIdTo,

            sourceSystem: selectorSourceSystem,

            sourceSystemId: selectorSourceSystemId,

            unitsFrom: selectorUnitsFrom,
            unitsTo: selectorUnitsTo,

            adjustBeginFrom: selectorAdjustBeginFrom,
            adjustBeginTo: selectorAdjustBeginTo,

            adjustEndFrom: selectorAdjustEndFrom,
            adjustEndTo: selectorAdjustEndTo,

            adjustNote: selectorAdjustNote,

            stakeholderId: selectorStakeholderId,

            contractId: selectorContractId,

            participations: selectorParticipations,

            residuals: selectorResiduals,

            royalties: selectorRoyalties
        };

        console.log(
            JSON.stringify(request, null, 2)
        );

        const hasSearchCriteria = Object.values(request).some(
            value =>
                value !== null &&
                value !== undefined &&
                value !== ''
        );

        // if (hasSearchCriteria) {
        //     setSubmitting(true);
        //     financialSearch(request)
        //

        //
        //         .then(data => {
        //
        //             console.log(data);
        //
        //             setFinancials(data);
        //
        //         })
        //
        //         .catch(err => {
        //
        //             console.error(err);
        //
        //         });
        //     setSubmitting(false);
        //
        // }
        if (hasSearchCriteria) {

            // setSubmitting(true);
            dispatchData(changeExternalData({ submitting: true }));

            financialSearch(request)

                .then(data => {

                    console.log(data);

                    setFinancials(data);

                    if (!data || data.length === 0) {
                        console.log("no matching records found");
                        dispatchData(changeExternalData({ snackbarOpen: true, snackbarSeverity: "info", snackbarMessage: "No matching records found" }));
                    }

                })

                .catch(err => {

                    console.error(err);

                })

                .finally(() => {

                    // setSubmitting(false);
                    dispatchData(changeExternalData({ submitting: false }));
                });

        }
        else {

            console.log(
                "search triggered, nothing to search."
            );
            dispatchData(changeExternalData({ snackbarOpen: true, snackbarSeverity: "warning", snackbarMessage: "No search criteria provided" }));
        }
    };

    // ****************** END FETCH ****************************

    const handleUploadFileDrawer = () => {
        console.log("upload file drawer state changed");
        dispatchData(changeUploadFile({uploadFileDrawerState: true,
            uploadFileDrawerMode: "Financial"}));
    };

    const renderFinancials = financials => {
        if (financials.length <= 0) {
            return "No Data";
        }}

    function handleControls(){
        setShowControls(!showControls)
    }

    // useEffect(() => {
    //     fetchFinancials();
    //     dispatchData(changeFinancial({financialGridRefresh: false}));
    // }, [useSelector((state) => state.financialSlc.value.financialGridRefresh)===true]);
    //

    useEffect(() => {

        if (selectorFinancialGridRefresh) {

            fetchFinancials();

            dispatchData(changeFinancial({
                financialGridRefresh: false
            }));
        }

    }, [selectorFinancialGridRefresh]);

    // ****************** BEGIN CRUD ***********************************

    const handleAdd = () => {
        dispatchData(changeFinancial({id: null, fileId: null,account: null, adjustFrom: null, adjustTo: null,
            adjustNote: null, amount: null, displayAmount: null, amountType: null, financialMarket: null,
            languageCode: null, licenseId: null, periodEndDate: null, propertyId: null,
            sourceSystem: null, sourceSystemId: null, territory: null, units: null,
            status: null,
            createdOn: null,createdBy:null,lastModifiedOn:null,lastModifiedBy:null,
            licenseAmount: null, licenseDisplayAmount: null, availBegin: null,
            availEnd: null, executionDate: null, licensee: null,
            propertyName: null, initialMarket: null, genre: null, productionType: null,
            financialDrawerState: true, financialDrawerMode: "Add"}));
    };

    function handleProcessFile(){
        setSubmitting(true);
        console.log("This is the file I will submit");
        console.log(selectedFileId);
        processFinancials(selectedFileId)
            .then(()=>{setSubmitting(false)})

    };

    const handleFileIdChange = (fileIdEvent) => {
        setSelectedFileId(fileIdEvent.target.value);
        console.log("This is for the selected Value");
        console.log(fileIdEvent.target.value);
    };

    const fetchFileIds = () => {

        getFileIds()
            .then(data => {
                console.log("Guilds");
                console.log(data);
                console.log("Guilds with empty string");
                data.unshift({singleField: ''});
                console.log(data);
                setFileIdDropdown(data);

            })
    }

    useEffect(() => {
        console.log("This is for the File Id dropdown...");
        fetchFileIds();
    }, []);


    const removeFinancial = () => {
        deleteFinancial(selectorFinancialId).then(() => {
            setDeleteOpen(false);
            dispatchData(changeFinancial({financialGridRefresh: true}));
            dispatchData(changeCrudSnackbar({crudOpen: true, crudSeverity: "success", crudMessage: "Record deleted"}));
        })
    }

    const handleDeleteOpen = () => {
        setDeleteOpen(true);
    };



    function DeleteConfirmationDialog() {

        const handleClickOpen = () => {
            setDeleteOpen(true);
        };

        const handleClose = () => {
            setDeleteOpen(false);
        };


        const handleDelete = () => {
            deleteFinancial(selectedId).then(() => {
                setDeleteOpen(false);
                dispatchData(changeFinancial({financialGridRefresh: true}));
                dispatchData(changeCrudSnackbar({crudOpen: true, crudSeverity: "success", crudMessage: "Record deleted"}));
            })
            console.log('Item deleted');
        };

        return (
            <Dialog
                open={deleteOpen}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'transparent', // makes backdrop transparent
                        boxShadow: '0 0 10px 5px rgba(0, 0, 0, 0.5)' // custom shadow box effect
                    }
                }}
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this item? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleDelete} autoFocus color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
    const handleDeleteCancel = () => {
        setDeleteOpen(false);
    };

    function handleSearch(){
        // dispatchData(changeFinancial({
        //     financialGridRefresh: false
        // }));
        setShowSearch(!showSearch)
    }


    const gridClear = () => {

        setFinancials([]);
    }

    // ****************** END CRUD ***********************************


    const handleFinancialInfo = () => {
        setFinancialInfoOpen(true);
    };

    const handleCloseFinancialInfo = () => {
        setFinancialInfoOpen(false);
    };



    // ****************** BEGIN DRILLDOWN ****************************

    // ****************** END DRILLDOWN ****************************

    const handleClickedRow = (financials) => {

        console.log('Financial data from clicked row');
        console.log(financials);
        dispatchData(changeFinancial({id: financials.id, fileId: financials.fileId,account: financials.account,
            adjustFrom: financials.adjustFrom, adjustTo: financials.adjustTo,adjustNote: financials.adjustNote,
            amount: financials.amount, displayAmount: financials.displayAmount, amountType: financials.amountType,
            financialMarket: financials.financialMarket, languageCode: financials.languageCode,
            licenseId: financials.licenseId, periodEndDate: financials.periodEndDate, propertyId: financials.propertyId,
            sourceSystem: financials.sourceSystem, sourceSystemId: financials.sourceSystemId,
            territory: financials.territory, units: financials.units,
            createdOn: financials.createdOn,createdBy:financials.createdBy,
            status: financials.status,
            lastModifiedOn:financials.lastModifiedOn,lastModifiedBy:financials.lastModifiedBy,
            licenseAmount: financials.licenseAmount, licenseDisplayAmount: financials.licenseDisplayAmount,
            availBegin: financials.availBegin, availEnd: financials.availEnd,
            executionDate: financials.executionDate, licensee: financials.licensee,
            propertyName: financials.propertyName, initialMarket: financials.initialMarket,
            genre: financials.genre, productionType: financials.productionType}));
    };

    // ****************** BEGIN RETURN ****************************

    return (
        <div style={{ height:800 , width: '100%' }}>
            {/*{submitting && <LinearIndeterminate/>}*/}
            {/*<Box sx={{height: 80, pt: 1, pl: 3}}>*/}
            {/*    <Stack direction={"horizontal"}>*/}
            {/*        <h3>Financials</h3>*/}
            {/*        <SearchIcon*/}
            {/*            sx={{pt: 3, ml: 2}}  color={'primary'}*/}
            {/*            //         sx={{mr: 3, verticalAlign: 'bottom'}}*/}
            {/*            //         fontSize="medium" color={'primary'}*/}
            {/*            onClick={handleSearch}*/}
            {/*        />*/}
            {/*        <Button sx={{pt: 2, ml: 5}}*/}
            {/*                onClick={handleUploadFileDrawer}*/}
            {/*        >Upload</Button>*/}
            {/*        <Box sx={{width:600}}/>*/}
            {/*        <Box sx={{pl: 10}}>*/}
            {/*            <FormControl variant="standard" sx={{pr:1, minWidth: 100 }} >*/}
            {/*                <InputLabel sx={{ ml: 1}} htmlFor="fileId">File ID</InputLabel>*/}
            {/*                <Select*/}
            {/*                    labelId="demo-simple-select-filled-fileId"*/}
            {/*                    id="fileId"*/}
            {/*                    FreeSolo={true}*/}
            {/*                    label="fileId"*/}
            {/*                    onChange={handleFileIdChange}*/}
            {/*                    sx={{ width: 300}}*/}
            {/*                >*/}
            {/*                    {fileIdDropdown.map((fileIdItem) =>*/}
            {/*                        <MenuItem  FreeSolo={true} value={fileIdItem.singleField}>{fileIdItem.singleField}</MenuItem>*/}
            {/*                    )}*/}
            {/*                </Select>*/}
            {/*            </FormControl>*/}
            {/*        </Box>*/}
            {/*        <Button sx={{pt: 2, ml: 1}}*/}
            {/*                onClick={handleProcessFile}*/}
            {/*        >Process</Button>*/}

            {/*    </Stack>*/}
            {/*</Box>*/}
            <Box sx={{height: 80, pt: 1, pl: 3}}>

                <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }} spacing={0}>

                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                        <Stack direction={"horizontal"}>

                            <Tooltip enterDelay={1000} enterNextDelay={1000} title={"Click for information."}>
                            <h3
                                style={{cursor:'pointer'}}   onClick={() => handleFinancialInfo()}
                            >Financials</h3>
                            </Tooltip>
                            {/*<SearchIcon*/}
                            {/*    sx={{pt: 2, ml: 2}}*/}
                            {/*    color={'primary'}*/}
                            {/*    onClick={handleSearch}*/}
                            {/*/>*/}

                                <FinancialInfoDialogue
                                    open={financialInfoOpen}
                                    onClose={handleCloseFinancialInfo}
                                />

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                    color="primary"
                                    size="large"               // large icon & hit area
                                    onClick={handleSearch}
                                    sx={{ ml: 2 }}             // any spacing you want
                                >
                                    <SearchIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                            <Button sx={{pt: 1, ml: 3}}
                                    onClick={handleUploadFileDrawer}
                            >Upload</Button>
                        </Stack>
                    </Box>

                    <Box sx={{ flex: 0, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                        {/*<Box sx={{width:600}}/>*/}
                        {/*                    <Box sx={{pl: 10}}>*/}
                        <Stack direction={"horizontal"}>
                            <FormControl variant="standard" sx={{pr:1, minWidth: 100 }} >
                                <InputLabel sx={{ ml: 1}} htmlFor="fileId">File ID</InputLabel>
                                <Select
                                    labelId="demo-simple-select-filled-fileId"
                                    id="fileId"
                                    FreeSolo={true}
                                    label="fileId"
                                    onChange={handleFileIdChange}
                                    sx={{ width: 300}}
                                >
                                    {fileIdDropdown.map((fileIdItem) =>
                                        <MenuItem  FreeSolo={true} value={fileIdItem.singleField}>{fileIdItem.singleField}</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            <Button sx={{pt: 2, ml: 1}}
                                    onClick={handleProcessFile}
                            >Process</Button>
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                </Stack>

            </Box>
            {showSearch && <FinancialSearch
                gridClear = {gridClear}
            />}
            {selectorUploadFileDrawerState && <UploadFileDrawer/>}
            {submitting && <LinearIndeterminate/>}
            {/*{submitting && <CircularProgress/>}*/}
            {selectorDrawerState && <FinancialDrawer
            />}
            {/*}*/}
            {gridVisible && <DataGrid
                experimentalFeatures={{ newEditingApi: true }}
                initialState={{
                }}
                aria-label={"Financials"}
                rows={financials}
                disableDensitySelector={true}
                disableColumnSelector={true}
                columns={FinancialGridColumns({
                    hideActions,
                    onEdit: () => dispatchData(changeFinancial({ financialDrawerState: true, financialDrawerMode: "Edit" })),
                    onClone: () => dispatchData(changeFinancial({ financialDrawerState: true, financialDrawerMode: "Clone" })),
                    onDelete: removeFinancial
                })}
                onRowClick = {(financials)=>handleClickedRow(financials.row)}
                density={"compact"}
                pageSize={20}
                rowsPerPageOptions={[20]}
                rowKey={financial => financial.id}
                components={{
                    Toolbar: AddFiltersExportToolbar,
                    Footer: CustomFooter,
                }}
                componentsProps={{
                    toolbar: {
                        handleAdd: handleAdd
                    }
                }}
            />}
            <CrudSnackbar/>
<NoSearchCriteriaSnackbar/>
            </div>

    );

    // ****************** END RETURN ****************************

}


